import React from "react";
import { Gift, PauseCircle } from "lucide-react";
import { useT } from "../../lib/LanguageContext";
import LoadFailure from "../../components/ui/LoadFailure";
import useAffiliateDashboard from "./useAffiliateDashboard";
import ShareLinkCard from "./ShareLinkCard";
import AffiliateStats from "./AffiliateStats";
import PayoutDestinationNotice from "./PayoutDestinationNotice";
import ReferralsTable from "./ReferralsTable";
import CommissionsTable from "./CommissionsTable";
import { affiliateErrorText } from "./errorText";

/**
 * The practitioner's affiliate page.
 *
 * ONE READ FOR THE HEADER, one paginated read per table. The dashboard read is the only
 * thing the top of the page states, and a failure of it withdraws every claim rather than
 * printing zeros: "you have earned nothing" and "we could not ask" are different facts and
 * the v0 page could not tell them apart (it had no `catch` at all — a failed read left the
 * spinner up forever).
 *
 * The two tables load independently, so one failing list does not blank the other or the
 * share links the practitioner came here for.
 */
export default function AffiliateAdmin() {
  const t = useT("admin");
  const tv = useT("common");
  const { dashboard, loading, loadError, reload, applyAccount } = useAffiliateDashboard();

  return (
    <div data-testid="affiliate-admin" className="space-y-6 max-w-5xl">
      <div>
        <h1 className="font-display text-4xl text-eh-ink flex items-center gap-3">
          <Gift className="w-6 h-6 text-sol-gold" /> {t("aff.title")}
        </h1>
        <p className="text-eh-muted text-sm mt-1">{t("aff.subtitle")}</p>
      </div>

      {loadError && (
        <LoadFailure
          message={affiliateErrorText(loadError, { t, tv, fallback: tv("errors.loadFailed") })}
          onRetry={reload}
          retryLabel={tv("actions.retry")}
          testId="affiliate-admin-load-error"
          retryTestId="affiliate-admin-retry"
        />
      )}

      {loading && !dashboard && !loadError && (
        <p className="text-sm text-eh-muted" data-testid="affiliate-admin-loading">{t("aff.loading")}</p>
      )}

      {dashboard && !loadError && (
        <>
          {!dashboard.program.enabled && (
            <p
              role="status"
              data-testid="affiliate-program-disabled"
              className="text-sm bg-amber-50 border border-amber-200 text-amber-900 rounded-2xl px-5 py-4 flex items-start gap-2"
            >
              <PauseCircle className="w-4 h-4 mt-0.5 shrink-0" /> {t("aff.banner.programDisabled")}
            </p>
          )}
          {dashboard.account.status === "disabled" && (
            <p
              role="status"
              data-testid="affiliate-account-disabled"
              className="text-sm bg-rose-50 border border-rose-200 text-rose-800 rounded-2xl px-5 py-4"
            >
              {t("aff.banner.accountDisabled")}
            </p>
          )}

          <ShareLinkCard
            account={dashboard.account}
            program={dashboard.program}
            t={t}
            tv={tv}
            onAccountChanged={applyAccount}
          />
          <AffiliateStats stats={dashboard.stats} t={t} />
          <PayoutDestinationNotice destinationReady={dashboard.payout.destinationReady} t={t} />
          <ReferralsTable />
          <CommissionsTable />
        </>
      )}
    </div>
  );
}
